import { api, type Recording } from "../lib/api.js";
import { RecordingUploader, blobSource, formatBytes, formatDuration } from "../lib/uploader.js";
import { startCheckout } from "../lib/billing.js";

const escapeHtml = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[c]!,
  );

const STATUS_LABEL: Record<Recording["status"], string> = {
  uploading: "Upload incomplete",
  uploaded: "Queued for analysis",
  analysing: "Analysing",
  ready: "Ready",
  failed: "Failed",
  // Not a stage on the way to anything: this stream is finished and is on the
  // streamer's own disk. "Ready" would imply we hold it and they could
  // download it from here.
  local: "Kept on your PC",
};

export async function recordingsView(root: HTMLElement) {
  root.innerHTML = `
    <header class="page-head">
      <h1>Recordings</h1>
      <div>
        <input type="file" id="file" accept="video/*" hidden>
        <button class="btn primary" id="pick">Upload a stream</button>
      </div>
    </header>
    <div id="uploads"></div>
    <div id="list" class="loading">Loading…</div>
  `;

  const list = root.querySelector<HTMLElement>("#list")!;
  const uploads = root.querySelector<HTMLElement>("#uploads")!;

  async function render() {
    const { recordings } = await api<{ recordings: Recording[] }>("GET", "/v1/recordings");
    if (!recordings.length) {
      list.className = "empty";
      list.innerHTML = `
        <div class="empty-icon" aria-hidden="true">⏺</div>
        <h2>No recordings yet</h2>
        <p>After a session, drop in the file OBS wrote. Stream-Cut lines it up with the
           reactions you marked live and cuts it into clips — you don't scrub anything.</p>
        <div class="actions">
          <button class="btn primary" id="emptyPick">Upload a stream</button>
        </div>
        <p class="hint">Large files resume if the upload drops.</p>`;
      list.querySelector("#emptyPick")?.addEventListener("click", () => fileInput.click());
      return;
    }

    list.className = "";
    list.innerHTML = recordings
      .map((r) => {
        // An incomplete upload is resumable, not lost — say so, because the
        // instinct otherwise is to delete and start the 15 GB over.
        const resumable = r.status === "uploading";
        return `<a class="row" href="${resumable ? "#" : `#/recordings/${r.id}`}">
          <div class="row-main">
            <strong>${r.filename ?? "Recording"}</strong>
            <span class="meta">
              ${r.recorded_at ? new Date(r.recorded_at).toLocaleDateString() : "—"}
              · ${r.size_bytes ? formatBytes(Number(r.size_bytes)) : "—"}
              ${Number(r.reaction_count) ? ` · ${r.reaction_count} reactions` : ""}
            </span>
          </div>
          <span class="status s-${r.status}">
            ${STATUS_LABEL[r.status]}${
              resumable && r.parts_total
                ? ` · ${Math.round((r.parts_complete / r.parts_total) * 100)}%`
                : ""
            }
          </span>
        </a>`;
      })
      .join("");
  }

  await render();

  // Recordings sit in analysing for a while; refresh so the user doesn't have
  // to guess whether anything is happening.
  const poll = setInterval(() => {
    if (!document.body.contains(root)) return clearInterval(poll);
    void render();
  }, 5000);

  // --- Upload -------------------------------------------------------------

  const fileInput = root.querySelector<HTMLInputElement>("#file")!;
  root.querySelector("#pick")!.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    fileInput.value = "";

    const card = document.createElement("div");
    card.className = "upload";
    card.innerHTML = `
      <div class="upload-head">
        <strong>${file.name}</strong>
        <button class="link" data-cancel>Cancel</button>
      </div>
      <div class="bar"><i></i></div>
      <div class="upload-meta">Starting…</div>`;
    uploads.append(card);

    const bar = card.querySelector<HTMLElement>(".bar i")!;
    const meta = card.querySelector<HTMLElement>(".upload-meta")!;

    /**
     * Turns a refusal into the offer.
     *
     * 402 and 413 are not failures — they are the product asking to be paid
     * for, at the strongest possible moment: the streamer has just finished a
     * broadcast and is holding a file full of reactions they want clips from.
     * A bare error here throws that away, and "try again" is worse than
     * useless because trying again cannot work.
     */
    const showPaywall = (message: string) => {
      card.classList.add("failed");
      meta.innerHTML = `${escapeHtml(message)}<br>
        <span class="meta">Your reactions are saved. This exact file will still
        generate every one of its clips.</span>`;
      const cta = document.createElement("button");
      cta.className = "btn";
      cta.textContent = "Subscribe and upload this";
      cta.addEventListener("click", async () => {
        cta.disabled = true;
        cta.textContent = "Opening…";
        try {
          const result = await startCheckout("creator", { api });
          if (result.mode === "redirect") {
            location.href = result.url;
            return;
          }
          // Paddle overlays this page instead of leaving it, and navigates
          // only once payment clears. Restore the button so closing the
          // overlay does not strand the streamer on a card they cannot retry.
          cta.disabled = false;
          cta.textContent = "Subscribe and upload this";
        } catch {
          // Whatever went wrong, the account page can explain it and offers
          // the same action. Dropping them there beats a dead button.
          location.hash = "#/account";
        }
      });
      card.append(cta);
    };

    let created: { recording_id: string; part_size: number; parts_total: number };
    try {
      // The limit is checked here, on create — not during the upload. This call
      // was outside the error handling, so a capped user got an uncaught
      // exception and a card stuck on "Starting…" with no message at all.
      created = await api("POST", "/v1/recordings", {
        filename: file.name,
        size_bytes: file.size,
        recorded_at: new Date(file.lastModified).toISOString(),
      });
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message = err instanceof Error ? err.message : String(err);
      if (status === 402 || status === 413) showPaywall(message);
      else {
        card.classList.add("failed");
        meta.textContent = message;
      }
      return;
    }

    const uploader = new RecordingUploader({
      source: blobSource(file),
      recordingId: created.recording_id,
      partSize: created.part_size,
      partsTotal: created.parts_total,
      getMissing: (limit) =>
        api("GET", `/v1/recordings/${created.recording_id}/upload?limit=${limit}`),
      reportParts: (parts) =>
        api("POST", `/v1/recordings/${created.recording_id}/parts`, { parts }),
      finalise: () => api("POST", `/v1/recordings/${created.recording_id}/complete`),
      onProgress: (p) => {
        const pct = (p.bytesUploaded / p.bytesTotal) * 100;
        bar.style.width = pct + "%";
        meta.textContent =
          `${formatBytes(p.bytesUploaded)} of ${formatBytes(p.bytesTotal)} · ` +
          `${p.rate ? formatBytes(p.rate) + "/s" : "—"} · ${formatDuration(p.etaSeconds)} left`;
      },
    });

    card.querySelector("[data-cancel]")!.addEventListener("click", () => uploader.abort());

    try {
      await uploader.run();
      meta.textContent = "Uploaded. Analysing…";
      card.classList.add("done");
      setTimeout(() => card.remove(), 4000);
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message = err instanceof Error ? err.message : String(err);
      if (status === 402 || status === 413) {
        showPaywall(message);
        return;
      }
      card.classList.add("failed");
      meta.textContent = `${message} — progress is saved; upload the same file again to resume.`;
    }
    void render();
  });
}
